// ── Within-quote propagation ─────────────────────────────────────────────────
// Fix one line, get offered the same fix on every other line in the SAME quote
// carrying the same defect.
//
// WHY THIS AND NOT MORE LEARNING. Every learning path in ElecBid is nightly and
// human-gated (match-learn → alias, quote-teach → lesson/template): a
// correction made now does nothing for the quote you are on. This is the other
// half — it never leaves the quote, never writes a rule, and is fully
// undoable, so it needs no evidence bar and no approval. It is not the engine
// getting smarter; it is the engine not making you type the same fix five
// times on one job.
//
// THE MATCH KEY IS THE REJECTED PRODUCT, NOT THE WORDING.
// Same insight the alias loop is built on. When the matcher put a solar ground
// clamp on line 3, it put that same row on lines 9 and 14 too — what links
// them is the book row they all landed on, which is exact, explains itself in
// one sentence ("3 other lines were also priced from that row"), and needs
// zero similarity scoring. Identical wording is accepted as a second key
// because two lines worded the same ARE the same request.
//
// Deliberately NOT matched: similar-but-different wording. That is fuzzy
// matching, and propagating a pick onto a line the tech did not look at is how
// a quote goes quietly wrong in bulk.

// Exported: offer-time maps (normalized wording → matched product) must key
// exactly the way sibling matching normalizes, or the two views drift.
export const normDesc = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const norm = normDesc;

const productOf = (m) => m?.master_product_id || m?._price_source?.master_product_id || null;

/**
 * Rebuild a matched-product view over any list: synthesize the match preview's
 * product (keyed by normalized wording) as _price_source on lines carrying no
 * pin. Offer AND apply must run siblingLinesFor over the SAME view — SA's
 * pre-Convert lines have no _price_source of their own, so a bare recompute
 * against the live list silently drops every "same book row" sibling the
 * offer counted.
 */
export function augmentMatches(list, matchedByDesc) {
  const arr = Array.isArray(list) ? list : [];
  if (!matchedByDesc) return arr;
  return arr.map((m) => {
    if (!m || m.master_product_id) return m;
    const mp = matchedByDesc[norm(m.description)];
    return mp ? { ...m, _price_source: { master_product_id: mp } } : m;
  });
}

/**
 * Lines that carried the same defect as the one just corrected.
 *
 * @param materials  the full array (indices are the caller's row indices)
 * @param idx        the line that was just fixed
 * @param before     that line's state BEFORE the fix (product + description)
 * @param after      what it was fixed to — used only to skip lines already there
 * @returns number[] sibling indices, never including idx
 */
export function siblingLinesFor(materials, idx, before, after) {
  const list = Array.isArray(materials) ? materials : [];
  const wasProduct = before?.master_product_id || null;
  const wasDesc = norm(before?.description);
  const nowProduct = after?.master_product_id || null;
  const out = [];
  for (let j = 0; j < list.length; j++) {
    if (j === idx) continue;
    const m = list[j];
    if (!m || !String(m.description || "").trim()) continue;
    // Already the right product — nothing to fix.
    if (nowProduct && productOf(m) === nowProduct) continue;
    // A line the tech pinned by hand is a deliberate choice; never overwrite it
    // in bulk on the strength of a different line's correction.
    if (m.master_product_id && m.master_product_id !== wasProduct) continue;
    const sameProduct = wasProduct && productOf(m) === wasProduct;
    const sameDesc = wasDesc.length > 2 && norm(m.description) === wasDesc;
    if (sameProduct || sameDesc) out.push(j);
  }
  return out;
}

/** One sentence explaining WHY these lines are being offered. */
export function siblingReason(count, before) {
  const n = `${count} other line${count === 1 ? "" : "s"}`;
  if (before?.master_product_id) return `${n} priced from that same book row`;
  return `${n} worded the same`;
}

/**
 * Provenance for a line priced from a book row the tech chose. ONE builder for
 * the pick itself and for its propagated siblings, with the same field names
 * the server matcher stamps — the editor's pick used to hand-roll this without
 * supplier_sku / supplier_description, so a picked line showed no part # and
 * the order + takeoff sheets had no supplier row to link back to.
 */
export function pickPriceSource(product, price) {
  return {
    tier: "supplier_prices",
    supplier_name: price?.supplier_name,
    supplier_sku: price?.supplier_sku || null,
    supplier_description: price?.supplier_description || null,
    master_product_id: product?.master_product_id || null,
    date_recorded: price?.date_recorded,
    confidence: "high",
    flag: null,
  };
}

/**
 * The patch to apply to each sibling. Description is rewritten too — the whole
 * point is that these lines were the same request; leaving the old wording
 * would send them back through the matcher blind on the next reprice.
 */
export function pickPatch(product, price) {
  return {
    description: price.display_description || price.supplier_description || product.description,
    our_cost: Number(price.unit_price) || 0,
    master_product_id: product.master_product_id || null,
    _price_source: {
      ...pickPriceSource(product, price),
      propagated: true,   // provenance: this row was fixed in bulk, not picked
    },
  };
}
